import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { BASE_URL } from "utils/constant";

const OrganizationDetailsForm = () => {
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showNamePopup, setShowNamePopup] = useState(true);
  const [showLogoPopup, setShowLogoPopup] = useState(false);
  const accessToken = localStorage.getItem("access");

  // Helper function to trim filename if it exceeds 100 characters
  const trimFileName = (file) => {
    if (file.name.length <= 100) {
      return file;
    }

    const extension = file.name.split(".").pop();
    const nameWithoutExtension = file.name.substring(
      0,
      file.name.lastIndexOf(".")
    );
    const maxNameLength = 100 - extension.length - 1; // -1 for the dot
    const trimmedName = nameWithoutExtension.substring(0, maxNameLength);

    // Create a new file with trimmed name
    const trimmedFile = new File([file], `${trimmedName}.${extension}`, {
      type: file.type,
      lastModified: file.lastModified,
    });

    return trimmedFile;
  };

  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: ["user-details"],
    queryFn: async () => {
      const { data } = await axios.get(
        `${BASE_URL}/auth/user-details-of-user/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken?.trim()}`,
          },
        }
      );
      return data;
    },
    enabled: !!accessToken,
  });

  const createOrganization = useMutation({
    mutationFn: async ({ userId, orgName, logoFile }) => {
      const form = new FormData();
      form.append("owner", userId);
      form.append("organization_name", orgName);
      form.append("active_flag", true);
      if (logoFile) {
        // Trim filename before appending
        const trimmedFile = trimFileName(logoFile);
        form.append("logo_image_file", trimmedFile);
      }

      const { data } = await axios.post(
        `${BASE_URL}/org/create-organization/`,
        form,
        {
          headers: {
            Authorization: `Bearer ${accessToken.trim()}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return data;
    },
    onSuccess: (data) => {
      localStorage.setItem("organization", JSON.stringify(data.data));
      toast.success("Organization created successfully");
      navigate("/");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create organization"
      );
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      // Validate file size (optional - e.g., 5MB limit)
      const maxSize = 15 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error("File size should be less than 15MB");
        return;
      }

      setLogoFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Show filename info if it was trimmed
      if (file.name.length > 100) {
        const trimmedFile = trimFileName(file);
        toast.info(`Filename was trimmed to: ${trimmedFile.name}`);
      }
    }
  };

  const handleRemoveImage = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleNext = () => {
    if (orgName.trim() === "") {
      toast.error("Organization name is required");
      return;
    }
    setShowNamePopup(false);
    setShowLogoPopup(true);
  };

  const handleSave = () => {

    if (!userData?.data?.id || orgName.trim() === "") {
      toast.error("Missing required information");
      return;
    }
    // console.log("Saving organization...");
    createOrganization.mutate({
      userId: userData.data.user,
      orgName,
      logoFile,
    });
  };

  return (
    <>
      {showNamePopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8">
            <h2 className="text-xl font-semibold text-center mb-6">
              Your Organization / Company Details
            </h2>
            {isUserLoading && (
              <p className="text-sm text-gray-500 text-center mb-4">
                Loading user details...
              </p>
            )}

            <label className="block text-sm font-medium mb-2" htmlFor="orgName">
              Organization Name
            </label>
            <input
              id="orgName"
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="enter name"
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 mb-6"
            />

            <button
              onClick={handleNext}
              className="w-full flex justify-center items-center bg-purple-700 hover:bg-purple-800 text-white py-2 rounded-md font-medium transition"
            >
              Next
              <span className="ml-2 text-lg">✔</span>
            </button>
          </div>
        </div>
      )}

      {showLogoPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8">
            <h2 className="text-xl font-semibold text-center mb-6">
              Almost there!
            </h2>

            <label className="block text-sm font-medium mb-2">
              Organization Name
            </label>
            <input
              type="text"
              value={orgName}
              readOnly
              className="w-full px-4 py-2 border rounded-md bg-gray-100 mb-6"
            />

            <label className="block text-sm font-medium mb-2">
              Organization Logo / Photo (Optional)
            </label>

            <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-md bg-gray-100 min-h-[150px] mb-6 relative">
              {logoPreview ? (
                <div className="relative w-full h-full flex items-center justify-center p-4">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-w-full max-h-32 object-contain rounded"
                  />
                  <button
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer py-8">
                  <Upload className="h-6 w-6 text-gray-500 mb-1" />
                  <span className="text-sm text-gray-600">Upload</span>
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {logoFile && (
              <div className="mb-4 p-2 bg-blue-50 rounded text-sm text-blue-700">
                <strong>Selected file:</strong>
                <br />
                <strong>Size:</strong>{" "}
                {(logoFile.size / 1024 / 1024).toFixed(2)} MB
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={isUserLoading || createOrganization.isPending}
              className="w-full flex justify-center items-center bg-purple-700 hover:bg-purple-800 text-white py-2 rounded-md font-medium transition disabled:opacity-50"
            >
              {createOrganization.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
              <span className="ml-2 text-lg">✔</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganizationDetailsForm;
